import React, { useState, useRef, useEffect } from 'react';
import { View, StyleSheet, PanResponder, Platform, TouchableWithoutFeedback } from 'react-native';
import { colores } from '../../../../styles/variablesCss';

const RangeSlider = ({ 
  minValue, 
  maxValue, 
  minLimit, 
  maxLimit, 
  onChange 
}) => {
  // Referencias para el layout y los panresponders
  const containerLayout = useRef({ width: 0, x: 0 });
  const sliderWidth = useRef(0);
  const minThumbOffset = useRef(0);
  const maxThumbOffset = useRef(0);

  // Configurar PanResponder para el thumb mínimo
  const minThumbPanResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onPanResponderGrant: (_, gestureState) => {
        if (maxLimit === minLimit) return;
        const thumbPosition = (minValue - minLimit) / (maxLimit - minLimit) * containerLayout.current.width;
        minThumbOffset.current = gestureState.x0 - (containerLayout.current.x + thumbPosition);
      },
      onPanResponderMove: (_, gestureState) => {
        if (containerLayout.current.width <= 0 || maxLimit === minLimit) return;
        
        // Calcular la posición relativa del dedo en el slider
        let relativePosition = gestureState.moveX - containerLayout.current.x - minThumbOffset.current;
        
        // Asegurar que la posición esté dentro del slider
        const clampedPosition = Math.max(0, Math.min(relativePosition, containerLayout.current.width));
        
        // Convertir la posición a un valor
        const percentage = clampedPosition / containerLayout.current.width;
        const priceRange = maxLimit - minLimit;
        let newValue = minLimit + percentage * priceRange;
        
        // Mantener una distancia mínima entre los thumbs (1% del rango)
        const minGap = priceRange * 0.01;
        
        // Asegurar que el valor esté dentro de los límites y mantenga la distancia con maxValue
        newValue = Math.max(minLimit, Math.min(newValue, maxValue - minGap));
        
        // Actualizar el valor
        onChange([newValue, maxValue]);
      }
    })
  ).current;

  // Configurar PanResponder para el thumb máximo
  const maxThumbPanResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onPanResponderGrant: (_, gestureState) => {
        if (maxLimit === minLimit) return;
        const thumbPosition = (maxValue - minLimit) / (maxLimit - minLimit) * containerLayout.current.width;
        maxThumbOffset.current = gestureState.x0 - (containerLayout.current.x + thumbPosition);
      },
      onPanResponderMove: (_, gestureState) => {
        if (containerLayout.current.width <= 0 || maxLimit === minLimit) return;
        
        // Calcular la posición relativa del dedo en el slider
        let relativePosition = gestureState.moveX - containerLayout.current.x - maxThumbOffset.current;
        
        // Asegurar que la posición esté dentro del slider
        const clampedPosition = Math.max(0, Math.min(relativePosition, containerLayout.current.width));
        
        // Convertir la posición a un valor
        const percentage = clampedPosition / containerLayout.current.width;
        const priceRange = maxLimit - minLimit;
        let newValue = minLimit + percentage * priceRange;
        
        // Mantener una distancia mínima entre los thumbs (1% del rango)
        const minGap = priceRange * 0.01;
        
        // Asegurar que el valor esté dentro de los límites y mantenga la distancia con minValue
        newValue = Math.min(maxLimit, Math.max(newValue, minValue + minGap));
        
        // Actualizar el valor
        onChange([minValue, newValue]);
      }
    })
  ).current;

  // Componente de Slider Web
  const WebSlider = () => {
    // Asegurarse que los valores se mantienen dentro de los límites exactos
    const safeMinValue = Math.max(minLimit, Math.min(minValue, maxValue));
    const safeMaxValue = Math.min(maxLimit, Math.max(maxValue, minValue));
    
    // Calcular porcentajes para posicionar los elementos
    const rangeSize = maxLimit - minLimit;
    const minPercent = rangeSize > 0 ? ((safeMinValue - minLimit) / rangeSize) * 100 : 0;
    const maxPercent = rangeSize > 0 ? ((safeMaxValue - minLimit) / rangeSize) * 100 : 100;
    const trackWidth = maxPercent - minPercent;
    
    return (
      <div className="web-slider-container" style={{width: '100%', position: 'relative', height: '20px', marginBottom: '20px'}}>
        <div className="slider-track" style={{position: 'absolute', height: '4px', background: '#E0E0E0', top: '50%', transform: 'translateY(-50%)', width: '100%'}}></div>
        <div 
          className="slider-track-active" 
          style={{
            position: 'absolute', 
            height: '4px', 
            background: colores.rojo, 
            top: '50%', 
            transform: 'translateY(-50%)', 
            left: `${minPercent}%`, 
            width: `${trackWidth}%`
          }}
        ></div>
        <div 
          className="thumb min-thumb" 
          style={{
            position: 'absolute', 
            height: '20px', 
            width: '20px', 
            borderRadius: '50%', 
            background: colores.rojo, 
            top: '50%', 
            transform: 'translate(-50%, -50%)', 
            left: `${minPercent}%`, 
            cursor: 'pointer'
          }}
          onMouseDown={(e) => handleThumbMouseDown(e, 'min')}
        ></div>
        <div 
          className="thumb max-thumb" 
          style={{
            position: 'absolute', 
            height: '20px', 
            width: '20px', 
            borderRadius: '50%', 
            background: colores.rojo, 
            top: '50%', 
            transform: 'translate(-50%, -50%)', 
            left: `${maxPercent}%`, 
            cursor: 'pointer'
          }}
          onMouseDown={(e) => handleThumbMouseDown(e, 'max')}
        ></div>
      </div>
    );
  };

  // Manejador de eventos de mouse para el slider web
  const handleThumbMouseDown = (e, thumbType) => {
    e.preventDefault();
    const startX = e.clientX;
    const startValue = thumbType === 'min' ? minValue : maxValue;
    const range = maxLimit - minLimit;
    const sliderRect = e.currentTarget.parentNode.getBoundingClientRect();
    const sliderWidth = sliderRect.width;
    
    // El espacio mínimo entre los thumbs (1% del rango total)
    const minGap = Math.max(1, range * 0.01); // Al menos 1 unidad de diferencia
    
    function handleMouseMove(e) {
      const dx = e.clientX - startX;
      const percent = dx / sliderWidth;
      const valueChange = percent * range;
      
      // Calcular el nuevo valor con precisión y aplicar restricciones de límites
      if (thumbType === 'min') {
        // Para el thumb mínimo, no debe superar (maxValue - minGap) ni ser menor que minLimit
        const newMinValue = Math.max(minLimit, Math.min(startValue + valueChange, maxValue - minGap));
        onChange([newMinValue, maxValue]);
      } else {
        // Para el thumb máximo, no debe ser menor que (minValue + minGap) ni superar maxLimit
        const newMaxValue = Math.min(maxLimit, Math.max(startValue + valueChange, minValue + minGap));
        onChange([minValue, newMaxValue]);
      }
    }
    
    function handleMouseUp() {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Componente de Slider Nativo mejorado para dispositivos móviles
  const NativeSlider = () => {
    // Mantener un estado local para el ancho del contenedor
    const [containerWidth, setContainerWidth] = useState(0);
    const [activeThumb, setActiveThumb] = useState(null); // Para seguimiento del pulgar activo
    
    // Asegurar que los valores iniciales estén dentro de los límites
    const safeMinValue = Math.max(minLimit, Math.min(minValue, maxValue));
    const safeMaxValue = Math.min(maxLimit, Math.max(maxValue, minValue));
    
  
    
    // Calcular posiciones absolutas para los elementos del slider
    const getThumbPosition = (value) => {
      if (containerWidth === 0 || maxLimit === minLimit) return 0;
      // Asegurar que el valor esté dentro de los límites
      const safeValue = Math.max(minLimit, Math.min(value, maxLimit));
      const percentage = (safeValue - minLimit) / (maxLimit - minLimit);
      return Math.max(0, Math.min(percentage * containerWidth, containerWidth));
    };
    
    // Función para calcular el valor basado en la posición del toque
    const getValueFromPosition = (position) => {
      if (containerWidth === 0 || maxLimit === minLimit) return minLimit;
      // Asegurar que la posición esté entre 0 y el ancho del contenedor
      const clampedPosition = Math.max(0, Math.min(position, containerWidth));
      const percentage = clampedPosition / containerWidth;
      // Calcular el valor exacto dentro de los límites
      return Math.max(minLimit, Math.min(maxLimit, minLimit + percentage * (maxLimit - minLimit)));
    };
    
    // Función para manejar los toques en el track del slider
    const handleTrackPress = (evt) => {
      if (containerWidth === 0 || maxLimit === minLimit) return;
      
      const touchX = evt.nativeEvent.locationX;
      const newValue = getValueFromPosition(touchX);
      
      // Determina qué thumb debe moverse (el más cercano al toque)
      const minDistance = Math.abs(getThumbPosition(minValue) - touchX);
      const maxDistance = Math.abs(getThumbPosition(maxValue) - touchX);
      
      // Mínimo espacio entre thumbs (1% del rango total o al menos 1 unidad)
      const minGap = Math.max(1, (maxLimit - minLimit) * 0.01);
            
      if (minDistance <= maxDistance) {
        // Mover el thumb mínimo, respetando el límite mínimo y manteniendo una distancia mínima con maxValue
        const newMin = Math.max(minLimit, Math.min(newValue, maxValue - minGap));
        onChange([newMin, maxValue]);
      } else {
        // Mover el thumb máximo, respetando el límite máximo y manteniendo una distancia mínima con minValue
        const newMax = Math.min(maxLimit, Math.max(newValue, minValue + minGap));
        onChange([minValue, newMax]);
      }
    };  
    
    // Usar los valores con restricciones aplicadas para asegurar posiciones correctas
    const minThumbPosition = getThumbPosition(safeMinValue);
    const maxThumbPosition = getThumbPosition(safeMaxValue);
    const trackActiveWidth = Math.max(0, maxThumbPosition - minThumbPosition);
    
    return (
      <View 
        style={styles.customSliderContainer}
        onLayout={(event) => {
          const {width, x} = event.nativeEvent.layout;
          containerLayout.current = {width, x};
          sliderWidth.current = width;
          setContainerWidth(width); // Guardar el ancho en el estado
        }}
      >
        <TouchableWithoutFeedback onPress={handleTrackPress}>
          <View style={styles.sliderTrackTouchable}>
            <View style={styles.sliderTrackBackground} />
            <View 
              style={[
                styles.sliderTrackActive, 
                {
                  left: minThumbPosition,
                  width: trackActiveWidth
                }
              ]} 
            />
          </View>
        </TouchableWithoutFeedback>
        
        <View 
          style={[
            styles.sliderThumb,
            styles.sliderThumbEnhanced, 
            { left: minThumbPosition }
          ]}
          {...minThumbPanResponder.panHandlers}
        >
          <View style={styles.sliderThumbInner} />
        </View>
        
        <View 
          style={[
            styles.sliderThumb, 
            styles.sliderThumbEnhanced,
            { left: maxThumbPosition }
          ]}
          {...maxThumbPanResponder.panHandlers}
        >
          <View style={styles.sliderThumbInner} />
        </View>
      </View>
    );
  };

  // Renderizar el slider adecuado según la plataforma
  return Platform.OS === 'web' ? <WebSlider /> : <NativeSlider />;
};

const styles = StyleSheet.create({
  customSliderContainer: {
    position: 'relative',
    width: '100%',
    height: 44, // Aumentar la altura para dar más espacio a los thumbs
    justifyContent: 'center',
    marginBottom: 10,
    marginTop: 10
  },
  sliderTrackTouchable: {
    position: 'absolute',
    width: '100%',
    height: 44, // Área de toque ampliada
    justifyContent: 'center'
  },
  sliderTrackBackground: {
    position: 'absolute',
    height: 4,
    width: '100%',
    backgroundColor: '#E0E0E0',
    borderRadius: 2,
  },
  sliderTrackActive: {
    position: 'absolute',
    height: 4,
    backgroundColor: colores.rojo,
    borderRadius: 2,
    // No definimos width o left aquí, se establecen dinámicamente
  },
  sliderThumb: {
    position: 'absolute',
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colores.rojo,
    transform: [{ translateX: -10 }, { translateY: -8 }],
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }
      : {
          elevation: 3,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.2,
          shadowRadius: 3,
        }
    ),
  },
  sliderThumbEnhanced: {
    // Para dispositivos móviles, hacemos la zona de toque más grande
    width: Platform.OS === 'web' ? 20 : 40,
    height: Platform.OS === 'web' ? 20 : 40,
    borderRadius: Platform.OS === 'web' ? 10 : 20,
    backgroundColor: 'rgba(255,255,255,0.5)', // Fondo semi-transparente para el área de toque
    transform: Platform.OS === 'web' ? 
      [{ translateX: -10 }, { translateY: -8 }] : 
      [{ translateX: -20 }, { translateY: -18 }], // Ajuste para centrar correctamente
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10 // Asegurarnos que esté sobre otros elementos
  },
  sliderThumbInner: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colores.rojo,
  }
});

export default RangeSlider;
